import React, { useState, useEffect, useCallback } from 'react';
import '../shared.css';
import Sidebar from '../Components/Layout/sidebar';
import Topbar from '../Components/Layout/topbar';
import Button from '../Components/Comman/Button';
import Modal from '../Components/Comman/Modal';
import {
  FieldGroup, TextInput, SelectInput, TextArea, FormGrid, FormActions, DeleteConfirm,
} from '../Components/Comman/FormFields';
import { supabase } from '../supabaseClient';

const STATUS_STYLE = {
  Published: { bg: 'rgba(74,148,74,0.14)',  color: '#2d7a2d' },
  Draft:     { bg: 'rgba(204,153,0,0.14)', color: '#8a6600' },
  Hidden:    { bg: 'rgba(42,42,42,0.1)',   color: '#555'    },
};

const STATUSES = ['Published', 'Draft', 'Hidden'];
const blank = () => ({ section: '', content: '', last_edited: 'Just now', status: 'Draft' });

/* ── Row ── */
const SectionRow = ({ section, last_edited, status, onEdit, onDelete }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Hidden;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', background: 'rgba(245,240,232,0.7)', borderRadius: 8,
      border: '1px solid rgba(42,42,42,0.08)',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div>
        <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 17, fontWeight: 600, color: '#191919' }}>{section}</p>
        <p style={{ fontFamily: 'Inter', fontSize: 11, color: '#8B8B7A', marginTop: 2 }}>Last edited: {last_edited}</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: s.bg, color: s.color }}>{status}</span>
        <button
          type="button"
          onClick={onEdit}
          style={{ background: '#0D0D0D', color: '#D6B893', border: 'none', borderRadius: 5, padding: '6px 14px', fontFamily: 'Inter', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#1a1a1a'}
          onMouseLeave={e => e.currentTarget.style.background = '#0D0D0D'}
        >Edit</button>
        <button
          type="button"
          onClick={onDelete}
          style={{ background: 'rgba(180,60,60,0.07)', border: '0.762px solid rgba(180,60,60,0.2)', borderRadius: 5, padding: '6px 14px', fontFamily: 'Inter', fontSize: 11.5, cursor: 'pointer', color: '#a33', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(180,60,60,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(180,60,60,0.07)'}
        >Delete</button>
      </div>
    </div>
  );
};

/* ── Form ── */
const SectionForm = ({ initial, onSave, onCancel, loading }) => {
  const [form, setForm] = useState(initial || blank());

  /* FIX: reset form when initial changes */
  useEffect(() => {
    setForm(initial || blank());
  }, [initial]);

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));
  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FieldGroup label="Section Name" required>
          <TextInput value={form.section} onChange={set('section')} placeholder="e.g. Hero Banner" required />
        </FieldGroup>
        <FieldGroup label="Content">
          <TextArea value={form.content || ''} onChange={set('content')} placeholder="Write the section content here…" rows={5} />
        </FieldGroup>
        <FormGrid>
          <FieldGroup label="Status" required>
            <SelectInput value={form.status} onChange={set('status')} options={STATUSES} required />
          </FieldGroup>
          <FieldGroup label="Last Edited Note">
            <TextInput value={form.last_edited} onChange={set('last_edited')} placeholder="Today, 10:30 AM" />
          </FieldGroup>
        </FormGrid>
        <FormActions onCancel={onCancel} submitLabel={initial?.id ? 'Update Section' : 'Add Section'} loading={loading} />
      </div>
    </form>
  );
};

/* ── Page ── */
const WebsiteContent = () => {
  const [sections, setSections]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [activeTab, setActiveTab] = useState('Sections');
  const [modal, setModal]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [toast, setToast]         = useState(null);
  const tabs = ['Sections', 'Media', 'SEO'];

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSections = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('website_sections').select('*').order('section');
    if (error) showToast('Failed to load: ' + error.message, true);
    else setSections(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const handleCreate = async (form) => {
    setSaving(true);
    const { id, ...rest } = form;
    const { error } = await supabase.from('website_sections').insert([rest]);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Section added ✓');
    setModal(null);
    fetchSections();
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    const { id, ...rest } = form;
    const { error } = await supabase.from('website_sections').update(rest).eq('id', id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Section updated ✓');
    setModal(null);
    fetchSections();
  };

  const handleDelete = async () => {
    setSaving(true);
    const { error } = await supabase.from('website_sections').delete().eq('id', selected.id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Section deleted ✓');
    setModal(null);
    fetchSections();
  };

  const closeModal = () => { setModal(null); setSelected(null); };

  return (
    <main>
      <Sidebar />
      <div className='pagestrcutre'>
        <Topbar />

        {toast && (
          <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 99999, background: toast.isError ? '#a33' : '#2d7a2d', color: '#fff', padding: '12px 20px', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
        )}

        <div className='pagetop_div'>
          <div className='pagetop_div1'>
            <h3 className='pagetop_text1'>Website Content</h3>
            {/* FIX: pass onClick to Button */}
            <Button
              text='+ Add Section'
              icon={null}
              onClick={() => { setSelected(null); setModal('create'); }}
            />
          </div>
          <p className='pagetop_text2'>Manage all public-facing content and pages</p>
        </div>

        <div className='page_content' style={{ paddingBottom: 30 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(214,184,147,0.3)', padding: 4, borderRadius: 8, width: 'fit-content' }}>
            {tabs.map(tab => (
              <button key={tab} type="button" onClick={() => setActiveTab(tab)} style={{
                padding: '7px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
                background: activeTab === tab ? '#0D0D0D' : 'none',
                color: activeTab === tab ? '#D6B893' : '#8B8B7A',
                transition: 'all 0.18s ease',
              }}>{tab}</button>
            ))}
          </div>

          {activeTab === 'Sections' && (
            loading ? (
              <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A' }}>Loading…</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sections.map(s => (
                  <SectionRow
                    key={s.id} {...s}
                    onEdit={() => { setSelected(s); setModal('edit'); }}
                    onDelete={() => { setSelected(s); setModal('delete'); }}
                  />
                ))}
                {sections.length === 0 && (
                  <p style={{ textAlign: 'center', padding: 40, fontFamily: '"Cormorant Garamond"', fontSize: 16, fontStyle: 'italic', color: 'rgba(13,13,13,0.4)' }}>No sections yet. Add one!</p>
                )}
              </div>
            )
          )}

          {activeTab === 'Media' && (
            <div style={{ background: 'rgba(214,184,147,0.4)', borderRadius: 10, padding: 40, textAlign: 'center' }}>
              <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 22, color: '#191919', fontStyle: 'italic' }}>Media library — connect your CDN or upload directly.</p>
              <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A', marginTop: 8 }}>Drag & drop images here or use the Media Library page.</p>
            </div>
          )}

          {activeTab === 'SEO' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Page Title',       value: 'Nebu Art Exhibition 2025 – Cairo'             },
                { label: 'Meta Description', value: 'Discover contemporary Egyptian art at Nebu…'  },
                { label: 'OG Image URL',     value: 'https://nebu.com/og-image.jpg'                },
                { label: 'Canonical URL',    value: 'https://nebu.com'                             },
              ].map(field => (
                <div key={field.label}>
                  <label style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#191919', display: 'block', marginBottom: 6 }}>{field.label}</label>
                  <input defaultValue={field.value} style={{ width: '100%', padding: '10px 14px', borderRadius: 6, border: '0.762px solid rgba(42,42,42,0.2)', background: '#F5F0E8', fontFamily: 'Inter', fontSize: 13, color: '#191919', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.currentTarget.style.borderColor = 'rgba(42,42,42,0.45)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'rgba(42,42,42,0.2)'}
                  />
                </div>
              ))}
              <button type="button" style={{ alignSelf: 'flex-start', marginTop: 8, background: '#0D0D0D', color: '#D6B893', border: 'none', borderRadius: 5, padding: '9px 20px', fontFamily: 'Inter', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Save SEO Settings</button>
            </div>
          )}
        </div>

        <Modal open={modal === 'create'} onClose={closeModal} title="Add Content Section" width={600}>
          <SectionForm onSave={handleCreate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'edit'} onClose={closeModal} title="Edit Content Section" width={600}>
          <SectionForm initial={selected} onSave={handleUpdate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'delete'} onClose={closeModal} title="Delete Section" width={420}>
          <DeleteConfirm label={selected?.section} onConfirm={handleDelete} onCancel={closeModal} loading={saving} />
        </Modal>
      </div>
    </main>
  );
};

export default WebsiteContent;